/**
 * Deployment Validation Schemas
 *
 * Validation schemas for deployment operations including version tagging,
 * custom tags, and rollback.
 *
 * CRITICAL: This file is adapted from tenant-api/src/validators/deployment.ts
 * Note: Uses local enum imports instead of backend models
 */

import { z } from 'zod';

// ============================================================================
// VERSION TAG VALIDATION
// ============================================================================

/**
 * Version tag validation schema
 * Format: Semantic versioning with optional 'v' prefix (e.g., "1.0.0", "v1.2.3")
 * Must be unique within an application, immutable once set
 */
export const versionTagSchema = z
  .string({
    required_error: 'Version tag is required',
    invalid_type_error: 'Version tag must be a string',
  })
  .min(1, 'Version tag is required')
  .max(20, 'Version tag must not exceed 20 characters')
  .regex(
    /^v?\d+\.\d+\.\d+$/,
    'Version tag must follow semantic versioning (e.g., v1.0.0 or 1.0.0)'
  );

export type VersionTagValidation = z.infer<typeof versionTagSchema>;

// ============================================================================
// CUSTOM TAG VALIDATION
// ============================================================================

/**
 * Single custom tag validation schema
 * Format: alphanumeric with hyphens and underscores (e.g., "stable", "production-ready")
 */
export const customTagSchema = z
  .string({
    required_error: 'Tag is required',
    invalid_type_error: 'Tag must be a string',
  })
  .min(1, 'Tag must be at least 1 character')
  .max(50, 'Tag must not exceed 50 characters')
  .regex(
    /^[a-zA-Z0-9_-]+$/,
    'Tag can only contain letters, numbers, hyphens, and underscores'
  );

export type CustomTagValidation = z.infer<typeof customTagSchema>;

/**
 * Custom tags array validation schema
 * Maximum 10 tags per deployment (checked against existing tags)
 */
export const customTagsArraySchema = z
  .array(customTagSchema)
  .min(1, 'At least one tag is required')
  .max(10, 'Cannot have more than 10 tags');

export type CustomTagsArrayValidation = z.infer<typeof customTagsArraySchema>;

// ============================================================================
// SET VERSION TAG FORM VALIDATION
// ============================================================================

/**
 * Set version tag form schema
 * Used in SetVersionTagModal
 */
export const setVersionTagFormSchema = z
  .object({
    versionTag: versionTagSchema,
  })
  .strict();

export type SetVersionTagFormValidation = z.infer<typeof setVersionTagFormSchema>;

// ============================================================================
// ADD CUSTOM TAGS FORM VALIDATION
// ============================================================================

/**
 * Add custom tags form schema
 * Used in AddCustomTagsModal
 */
export const addCustomTagsFormSchema = z
  .object({
    customTags: customTagsArraySchema,
  })
  .strict();

export type AddCustomTagsFormValidation = z.infer<typeof addCustomTagsFormSchema>;

// ============================================================================
// ROLLBACK FORM VALIDATION
// ============================================================================

/**
 * Rollback reason validation schema
 * Reason is required to track why a deployment was rolled back
 */
export const rollbackReasonSchema = z
  .string({
    required_error: 'Reason is required',
    invalid_type_error: 'Reason must be a string',
  })
  .min(1, 'Reason is required')
  .max(500, 'Reason must not exceed 500 characters')
  .trim();

export type RollbackReasonValidation = z.infer<typeof rollbackReasonSchema>;

/**
 * Rollback form schema
 * Used in RollbackModal
 */
export const rollbackFormSchema = z
  .object({
    reason: rollbackReasonSchema,
  })
  .strict();

export type RollbackFormValidation = z.infer<typeof rollbackFormSchema>;
